import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { AppState } from '../../../store/state.model';
import { SnackBarService } from "../../../common/services/snack-bar/snack-bar.service";
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { Packages } from '../vehicle-summary/vehicle-summary.component';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { Observable, Subscription } from 'rxjs';
import { ApiResponseActions } from '../../../store/api-response/api-response-actions';
import { AdminDispatcher } from '../../../store/admin-store/admin-dispatcher';
import { SidenavService } from '../../services/sidenav.service';
import { FinegrainRolesService } from '../../../common/services/finegrain-roles/finegrain-roles.service';
import { ShowAddTrialServicesModalComponent } from '../show-add-trial-services-modal/show-add-trial-services-modal.component';
import { MatDialog } from '@angular/material';
import { StepState } from '../../../store/steps-store/step-state.model';
import { EligibleGenerationsForDisclaimer, EligibleProductsForDisclaimer } from '../../constants/subscription_constants';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { AdvancedSubscriptionFeaturesComponent } from './advanced-subscription-features/advanced-subscription-features.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-subscription-details',
    templateUrl: './subscription-details.component.html',
    styleUrls: ['./subscription-details.component.scss'],
    imports: [NgIf, MatIcon, NgFor, AdvancedSubscriptionFeaturesComponent, TranslatePipe, UpperCasePipe]
})
export class SubscriptionDetailsComponent implements OnInit, OnDestroy {
  public hasError: boolean = false;
  /**
   *@property flag to know if the wifi is enabled
   **/
  public isWifiEnabled: boolean;
  /**
   *@property property to store if wifi request success
   **/
  public wifiSuccess;
  /**
   *@property property to store if wifi request error
   **/
  public wifiError;
  /**
   *@property apiObservable Property to observe the redux customer store
   **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;

  /**
   * @property page status
   */
  @Input()
  public pageStatus = '';
  /**
   * @property type of active services paid or trial
   */
  @Input()
  public type;
  /**
   * @property subscriptions
   */
  @Input()
  public subscriptions: object[];
  /**
   * @property vehicleData
   */
  @Input()
  public vehicleData: object;
  /**
   * @property to store params
   */
  @Input()
  public params: object;
  /**
   * @property to store the customer details
   */
  public customer: object;
  /**
   * @property wifiState to store the wifi state for the vehicle imei
   */
  public wifiState: object;
  /**
   * @property productGroup to store the group of products with subscription details
   */
  productGroup: string[] = [];
  /**
   * @property listOfProducts to store all the list of all available products
   */
  public listOfProducts: Array<any> = [];
  /**
   * @property groupedProducts to group the products based on the subscription
   */
  groupedProducts: any;
  /**
   * @property enableSubscription will give the info whether to enable the subscription or not
   */
  public enableSubscription: boolean;
  /**
   * @property enablePreview will give the info whether to enable the preview or not
   */
  public enablePreview: boolean;
  /**
   * @property sub hold subscription
   */
  public sub: Subscription;
  /**
   * @property previewData hold preview results data
   */
  public previewData: object;
  /**
   * @property isPreviewDataAvailable specifies whether preview data is available or not
   */
  public isPreviewDataAvailable: boolean;
  /**
   * @property trialSubscriptionsExists specifies whether trial subscriptions exists or not in the sub preview
   */
  public trialSubscriptionsExists = false;
  /**
   *@property to subscribe to step store
   **/
  @select('step') readonly stepObservable: Observable<StepState>;
  /**
   * @property subscription hold Subscription
   */
  public stepSubscription: Subscription;

  /**
   * @property isPreviewOrSubscriptionsAvailable specifies whether subscriptions  or trial preview package are available. 
   */
  public isPreviewOrSubscriptionsAvailable = false;


  /**
   * @property to hold the vehicle association Active GUID
   */
  public vehicleAssociationActiveGuid = '';

  /**
   * @property to store the flag for to show the disclaimer message for the safety and service connect product.
   */
  public showDisclaimerMessage = false;

  /**
   * @property to store the value of the model year.
   */
  public modelYear;
  /**
   * @property to store the value of the vehicle generation.
   */
  public vehicleGeneration = '';

  preview: {};

  constructor(
    private snackBarService: SnackBarService,
    public translate: TranslationService,
    public reduxState: NgRedux<AppState>,
    public orch: OrchestrationService,
    public apiDispatcher: ApiResponseDispatcher,
    private adminDispatcher: AdminDispatcher,
    private sidenav: SidenavService,
    public finegrainRolesService: FinegrainRolesService,
    public dialog: MatDialog
  ) {
    this.sub = this.apiObservable.subscribe((api) => {
      let temp =
        api.apiAction === ApiResponseActions.SET_PREVIEW &&
        api.preview['type'] === 'success' &&
        api.preview['vin'] === this.vehicleData['vin'] &&
        !!api.preview['payload']
          ? this.previewSuccess(api.preview['payload'])
          : api.preview['type'] === 'error'
          ? this.previewError(api.preview)
          : null;
      temp =
        api.apiAction === ApiResponseActions.SET_WIFI_STATUS &&
        api.wifiState['type'] === 'success' &&
        api.wifiState['payload'] &&
        api.wifiState['payload']['wifiCurrentState']
          ? (this.wifiState = api.wifiState['payload'])
          : {};
      if (api.apiAction === ApiResponseActions.SET_WIFI_STATUS && api.wifiState['type'] === 'error') {
        this.wifiError = true;
      }
    });
  }

  public getSubscriptionHistoryResetFlag (): void {
    this.toggleSidenav();
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.stepSubscription) {
      this.stepSubscription.unsubscribe();
    }
  }
  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   */
  ngOnInit() {
    const {
      customer: { data },
    } = this.reduxState.getState();
    this.customer = data;
    this.getWifiStatus(
      this.vehicleData['imei'],
      this.vehicleData['vin'],
      this.vehicleData['generation'],
      this.vehicleData['subscriberGuid']
    );
    this.wifiState = this.reduxState.getState().api.wifiState['payload'];
    this.vehicleAssociationActiveGuid = '';
    this.stepSubscription = this.stepObservable.subscribe((step) => {
      if (!!step.vehicleAssociationDetails['isVehicleAssociated']) {
        this.vehicleAssociationActiveGuid =
          step.vehicleAssociationDetails['vehicleAssociatedGuid'];
      }
    });

    this.preview = {};
    this.vehicleGeneration = this.vehicleData['generation'] && this.vehicleData['generation'];
    this.modelYear = this.vehicleData['modelYear'] && parseInt(this.vehicleData['modelYear']);
  }

  /**
   * @function ngOnChanges Method to check for any model changes in the component
   */
  ngOnChanges() {
    this.checkSubscriptions();
  }
  /**
   *@function parseDate Will return the values of the date in the next format
   *@example '08/13/2022'
   *@param date Will recive a date in the next format
   *@example '2018-12-31'
   **/
  public parseDate(date: string): string {
    const year = date.split('-')[0];
    const month = date.split('-')[1];
    const day = date.split('-')[2];
    return `${month}/${day}/${year}`;
  }
  /**
   * @function getPreview get preview
   */
  public getPreview(): void {
    if (
      this.vehicleData['generation'] &&
      !(
        this.vehicleData['generation'] === 'PRE17CY' ||
        this.vehicleData['generation'] === '17CY'
      )
    ) {
      this.orch.getPreview({
        type: 'preview',
        params: this.getPackageHeaders(this.vehicleData),
      });
    }
  }
  /**
   * @function getSubscriptionHistory gets the subscription history of the account
   * @param vin Vin number of the vehicle
   */
  public getSubscriptionHistory(): void {
    this.toggleSidenav();
  }
  /**
   * @function getPackageHeaders get the headers for getting the packages by REST Client
   * @returns {void}
   **/
  private getPackageHeaders(vehicleData): Object {
    return {
      asicode: vehicleData['asiCode'],
      hwType: !!vehicleData['hwType']
        ? vehicleData['hwType']
        : vehicleData['dcm'] && vehicleData['dcm']['hardwareType'],
      generation: vehicleData['generation'],
      region: vehicleData['region'],
      brand: vehicleData['brand']
        ? vehicleData['brand']
        : ['TOY', 'T'].indexOf(vehicleData['brandCode']) > -1
        ? 'T'
        : ['SUB', 'S'].indexOf(vehicleData['brandCode']) > -1
        ? 'S'
        : 'L',
      vin: vehicleData['vin'],
      guid: vehicleData['subscriberGuid'],
    };
  }
  /**
   * @function previewSuccess success method of get preview
   */
  public previewSuccess(response): void {
    if (!!response['services'] && response['services'].length > 0) {
      if (!response['subscriptions']) {
        response['subscriptions'] = [];
      }
      response['services'].forEach((obj) => {
        response['subscriptions'].push(obj);
      });
    }
    if (!!response['subscriptions'] && response['subscriptions'].length > 0) {
      this.apiDispatcher.setPreview({});
      this.addCpoTrialForSubpreview(response['subscriptions']);
      if (
        this.vehicleData['generation'].toLowerCase() === '21mm' &&
        this.vehicleData['region'].toLowerCase() === 'us'
      ) {
        this.calculatePPOTrial(response['subscriptions']);
      }
      if (response['subscriptions']) {
        this.setPreviewResults(response['subscriptions']);
        this.enablePreview = true;
        this.isPreviewOrSubscriptionsAvailable = this.enablePreview && this.preview['trialPreview'].length > 0;
        this.handleDisclaimerMessage(this.preview['trialPreview'].map(item => item.productName));
        const listOfTrialSubscriptions = response['subscriptions'].filter(
          (product) => product['type'].toLowerCase() === 'trial'
        );
        this.trialSubscriptionsExists = listOfTrialSubscriptions.length > 0;
      }
    }
  }
  public previewError(error) {
    this.hasError = true;
    this.apiDispatcher.setPreview({});
    this.snackBarService.showToast('Subscription details failed to load.');
  }

  /**
   * @function checkSubscriptions check for existing subscriptions
   */
  public checkSubscriptions(): void {
    const guid = this.vehicleData['subscriberGuid'];
    this.groupedProducts = {};
    if (this.reduxState.getState().common.profile === 'vehicle' && !!guid) {
      this.orch.searchCustomer('subscribed', { guid });
    }
    if (
      !!this.vehicleData['services'] &&
      this.vehicleData['services'].length > 0
    ) {
      if (!this.vehicleData['subscriptions']) {
        this.vehicleData['subscriptions'] = [];
      }
      this.vehicleData['services'].forEach((obj) => {
        this.vehicleData['subscriptions'].push(obj);
      });
    }
    if (!!guid && this.vehicleData['subscriptions']) {
      this.addCpoTrialForSubscribed(this.vehicleData['subscriptions']);
      this.listOfProducts = this.vehicleData['subscriptions']
        .map((item) => item.productName)
        .filter((value, index, self) => self.indexOf(value) === index);
      this.filterProductsList();
      this.listOfProducts.forEach((product) => {
        this.productGroup = [];
        this.vehicleData['subscriptions'].forEach((element) => {
          if (
            product === element.productName &&
            (element.type.toLowerCase() === 'trial' ||
              element.type.toLowerCase() === 'paid' ||
              element.type.toLowerCase() === 'free' ||
              element.type.toLowerCase() === 'ppotrial' ||
              element.type.toLowerCase() === 'cpotrial' ||
              element.type.toLowerCase() === 'standardwithcpotrial' ||
              !!element.goodwillIssuedFor)
          ) {
            if (element['type'] === 'Trial' && this.productGroup.length > 0) {
              this.productGroup.unshift(element);
            } else {
              this.productGroup.push(element);
            }
            this.groupedProducts[product] = this.productGroup;
          }
        });
      });
    }
    this.enableSubscription = Object.keys(this.groupedProducts).length > 0;
    this.isPreviewOrSubscriptionsAvailable = this.enableSubscription;
    this.handleDisclaimerMessage(this.listOfProducts);
    if (!this.enableSubscription) {
      this.getPreview();
    }
  }
  /**
   * @method toggleSidenav toggles the side nav
   **/
  public toggleSidenav(): void {
    this.sidenav.toggle();
    this.adminDispatcher.setUpdateInfo(
      'subscription-history',
      'subscription-history',
      'subscription-history',
      '',
      { vehicleDetails: this.vehicleData }
    );
    window.scrollTo(0, 0);
  }

  /**
   * getWifiStatus action to find vehicle (api call)
   **/
  public getWifiStatus(
    imei: string,
    vin: string,
    generation: string,
    guid: string
  ): void {
    this.orch.getWifiStatusRequest({ imei, vin, generation, guid });
  }

  /**
   * @method isSubscriptionOn true when the vehicle is subscribed or active
   */
  public isSubscriptionOn(): boolean {
    const status = this.vehicleData && this.vehicleData['subscriptionStatus'];
    if (!status) {
      return false;
    }
    const normalized = String(status).toLowerCase();
    return normalized === 'active' || normalized === 'subscribed';
  }

  /**
   * @method getsubscriptionStatus used to get the translation of the subscription status
   */
  public getSubscriptionStatus(status: string): string {
    return status.toLowerCase() === 'active' ||
      status.toLowerCase() === 'subscribed'
      ? this.translate.getCustomTranslation(
          'subscription-details.labels.subscribed'
        )['value']
      : status;
  }
  /**
   * @method filterProductsList checks the generation to see if Dynamic Navigation should be hidden
   */
  public filterProductsList(): void {
    if (this.vehicleData['generation'].toLowerCase() === '17cy') {
      this.listOfProducts = this.listOfProducts.filter(
        (product) => product.toLowerCase() !== 'dynamic navigation'
      )
    }
  }
  /**
   * @method setPreviewResults checks the generation to see if Dynamic Navigation should be hidden for preview and then sets preview results
   */
  public setPreviewResults(response: Array<any>): void {
    const is17CY = this.vehicleData['generation'].toLowerCase() === '17cy';
    this.preview['trialPreview'] = response.filter(
      (pack) =>
        pack.type &&
        (pack.type.toLowerCase() === 'trial' ||
          pack.type.toLowerCase() === 'free') &&
        (!is17CY || pack.productName.toLowerCase() !== 'dynamic navigation')
    );
    this.preview['paidPreview'] = response.filter(
      (pack) =>
        pack.type &&
        pack.type.toLowerCase() === 'paid' &&
        (!is17CY || pack.productName.toLowerCase() !== 'dynamic navigation')
    );
    this.preview['goodWillSub'] = response.filter(
      (pack) =>
        !!pack.goodwillIssuedFor &&
        (!is17CY || pack.productName.toLowerCase() !== 'dynamic navigation')
    );
  }

  /**
   * @method getGoodwillPackages toggles the side nav to open the give Goodwill side nav
   */
  public getGoodwillPackages() {
    this.adminDispatcher.setUpdateInfo('give-goodwill', '', '', '');
    this.sidenav.toggle();
    window.scrollTo(0, 0);
  }

  /**
   * @function getPermission get Permissions to display goodwill button or not
   */
  public getPermission(feature: string): boolean {
    return this.finegrainRolesService.getCustomPermissions(feature);
  }

  /**
   * @function showAddTrialServicesModal Opens the Modal pop up which shows the info regarding the trail services availability
   **/
  public showAddTrialServicesModal(): void {
    this.dialog.open(ShowAddTrialServicesModalComponent, {
      width: '38.9375em',
      height: '16.875em',
    });
  }

  /**
   * @function getProductTermUnitWithTypeLabel it will return the proper language keys based on product term value
   **/
  public getProductTermUnitWithTypeLabel(product) {
    if (product['term'] >= 2) {
      return (
        'subscription-details.labels.' +
        product['termUnit'].toLowerCase() +
        's' +
        '-' +
        product['type'].toLowerCase()
      );
    }
    return (
      'subscription-details.labels.' +
      product['termUnit'].toLowerCase() +
      '-' +
      product['type'].toLowerCase()
    );
  }

  /**
   * @function handleDisclaimerMessage it checks and returns the flag to display a disclaimer for Eligible Product.
   **/
  public handleDisclaimerMessage(productList) {  
    const hasEligibleProductsForDisclaimer = productList ? EligibleProductsForDisclaimer.some(product => productList.includes(product)) : false; 
    const generation = this.vehicleData['generation'].toLowerCase();
    if (EligibleGenerationsForDisclaimer.includes(generation) && hasEligibleProductsForDisclaimer) {
      this.showDisclaimerMessage = true;
    } else {
      this.showDisclaimerMessage = false;
    }
  }

  /**
   * @function getDisplayTermString returns the formatted display term
   **/
  public getDisplayTermString(product) {
    if (product['displayTerm'].split('-')[1]) {
      var term = product['displayTerm'].split('-')[1].trim();
      var termArray = term.split(' ');
      termArray.pop();
      return 'Term: ' + termArray.join(' ') + ' Standard Trial';
    }
  }

  /**
   * @function addCpoTrialForSubpreview to add the CPO trial for an existing trial products
   **/
  public addCpoTrialForSubpreview(previewObject) {
    const cpoTrialProductNames = [];
    previewObject.forEach((ele) => {
      if (ele.type === 'CPOTrial') {
        cpoTrialProductNames.push(ele.productName);
      }
    });
    const trialProductNames = [];
    previewObject.forEach((ele) => {
      if (ele.type === 'Trial') {
        trialProductNames.push(ele.productName);
      }
    });
    previewObject.forEach((ele, index) => {
      if (
        cpoTrialProductNames.includes(ele.productName) &&
        ele.type === 'Trial'
      ) {
        const displayTermArray = ele.displayTerm
          .split('-')[1]
          .trim()
          .split(' ');
        if (displayTermArray[1] === 'days' || displayTermArray[1] === 'day') {
          previewObject[index]['displayTerm'] =
            'Term - 3 months ' + ele.displayTerm.split('-')[1].trim();
        } else if (displayTermArray[1] === 'month') {
          const length = ele.displayTerm.split('-')[1].trim().length;
          const temp = ele.displayTerm.split('-')[1].trim().substr(7, length);
          previewObject[index]['displayTerm'] =
            'Term - ' + 2 + parseInt(displayTermArray[0]) + ' months' + temp;
        } else if (
          displayTermArray[1] === 'months' &&
          parseInt(displayTermArray[0]) < 3
        ) {
          const length = ele.displayTerm.split('-')[1].trim().length;
          const temp = ele.displayTerm.split('-')[1].trim().substr(7, length);
          previewObject[index]['displayTerm'] =
            'Term - ' + 1 + parseInt(displayTermArray[0]) + ' month' + temp;
        }
      } else if (
        cpoTrialProductNames.includes(ele.productName) &&
        ele.type === 'CPOTrial'
      ) {
        if (!trialProductNames.includes(ele.productName)) {
          previewObject[index]['type'] = 'Trial';
        }
      }
    });
  }

  /**
   * @function addCpoTrialForSubscribed to add the CPO trial for an existing subscribed trial products
   **/
  public addCpoTrialForSubscribed(subscribedObject) {
    let subscribedCpoTrialProducts = [];
    subscribedObject.forEach((ele) => {
      if (ele.type === 'CPOTrial') {
        subscribedCpoTrialProducts.push(ele);
      }
    });
    if (subscribedCpoTrialProducts.length > 0) {
      subscribedCpoTrialProducts.forEach((ele) => {
        let productIndex = subscribedObject.findIndex(
          (product) =>
            product['productName'] === ele['productName'] &&
            product['type'] === 'Trial'
        );
        if (productIndex > -1) {
          subscribedObject[productIndex]['subscriptionEndDate'] =
            ele['subscriptionEndDate'];
          subscribedObject[productIndex]['subscriptionRemainingDays'] =
            subscribedObject[productIndex]['subscriptionRemainingDays'] +
            ele['subscriptionRemainingDays'];
          subscribedObject[productIndex]['type'] = 'standardWithCPOTrial';
          let Index = subscribedObject.findIndex(
            (product) =>
              product['productName'] === ele['productName'] &&
              product['type'] === 'CPOTrial'
          );
          subscribedObject.splice(Index, 1);
        } else {
          let productIndex = subscribedObject.findIndex(
            (product) => product['subscriptionID'] === ele['subscriptionID']
          );
          if (productIndex > -1) {
            ele['type'] = 'CPOTrial';
          }
        }
      });
    }
  }

  /**
   * @function calculatePPOTrial to add the PPO trial
   **/
  public calculatePPOTrial(response) {
    let productsWithPPOTrial = [];
    response.forEach((ele) => {
      if (ele.type === 'PPOTrial') {
        productsWithPPOTrial.push(ele);
      }
    });
    response.forEach((ele) => {
      if (ele.productName && ele.type === 'Trial') {
        let ppoIndex = productsWithPPOTrial.findIndex(
          (e) => e.productName === ele.productName
        );
        if (ppoIndex > -1) {
          var term = ele['displayTerm'].split('-')[1].trim();
          var termArray = term.split(' ');
          termArray.pop();
          var ppoTerm = productsWithPPOTrial[ppoIndex]['displayTerm']
            .split('-')[1]
            .trim();
          ele['displayPPOTerm'] = `Term: ${termArray.join(
            ' '
          )} Standard Trial + ${ppoTerm} Extended Trial`;
          productsWithPPOTrial.splice(ppoIndex, 1);
        }
      }
    });
    if (productsWithPPOTrial.length > 0) {
      productsWithPPOTrial.forEach((ele) => {
        ele['type'] = 'Trial';
        var term = ele['displayTerm'].split('-')[1].trim();
        ele['displayPPOTerm'] = 'Term: ' + term + ' Extended Trial';
        let indexFound = response.findIndex(
          (obj) => obj.productName === ele.productName
        );
        if (indexFound < 0) {
          response.push(ele);
        }
      });
    }
  }

  /**
   * @method openDataConsents opens the side nav with Data Consents list
   **/
  public openDataConsents(): void {
    this.adminDispatcher.setUpdateInfo('data-consents', 'data-consents', 'data-consents', '', { vehicleDetails: this.vehicleData });
    this.sidenav.toggle();
    window.scrollTo(0, 0);
  }

    /**
   * @method openEmergencyContact opens the side nav with Emergency Contact
   **/
    public openEmergencyContact(): void {
      this.adminDispatcher.setUpdateInfo('emergency-contact', 'emergency-contact', 'emergency-contact', '', { vehicleDetails: this.vehicleData });
      this.sidenav.toggle();
      window.scrollTo(0, 0);
    }

}
